import Button from '@material-ui/core/Button';
import Checkbox from '@material-ui/core/Checkbox';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import FormControl from '@material-ui/core/FormControl';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import FormGroup from '@material-ui/core/FormGroup';
import { makeStyles } from '@material-ui/core/styles';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import TextField from '@material-ui/core/TextField';
import Typography from '@material-ui/core/Typography';
import Fuse from 'fuse.js';
import React from 'react';

import * as constants from '../../../constants';
import * as colors from '../../../styles/colors';
import getUserLevelAvatar from '../../../utils/getUserLevelAvatar';

const UsersSearchDialog = ({ open, onClose, onUserClick = () => null, week, showHours = true, users }) => {
  const [database, setDatabase] = React.useState(null);
  const [search, setSearch] = React.useState({ query: '', results: [] });
  const [filters, setFilters] = React.useState({ levelOne: false, levelTwo: false, levelThree: false });

  React.useEffect(
    () => {
      if (open) {
        setDatabase(new Fuse(
          users,
          {
            shouldSort: true,
            threshold: 0.1,
            location: 0,
            distance: 100,
            maxPatternLength: 32,
            minMatchCharLength: 1,
            keys: ['displayName', 'userId']
          }
        ));
        setSearch({ query: '', results: [] });
        setFilters({ levelOne: false, levelTwo: false, levelThree: false });
      }
    },
    [open]
  );

  const queryDatabase = event => {
    const query = event.target.value;
    setSearch({
      query,
      results: database.search(query)
        .filter(result => passesFilter(result))
        .slice(0, 10)
    });
  };

  const passesFilter = user => {
    if (filters.levelOne || filters.levelTwo || filters.levelThree) {
      return (filters.levelOne && user.officerLevel === constants.officerLevels.LEVEL_1.value)
        || (filters.levelTwo && user.officerLevel === constants.officerLevels.LEVEL_2.value)
        || (filters.levelThree && user.officerLevel === constants.officerLevels.LEVEL_3.value);
    } else {
      return true;
    }
  };

  const sortUsers = (a, b) => {
    if (a.shiftApplicant && !b.shiftApplicant) {
      return -1;
    } else if (!a.shiftApplicant && b.shiftApplicant) {
      return 1;
    }
    const aOfficerLevel = getUserLevelAvatar(a);
    const bOfficerLevel = getUserLevelAvatar(b);
    if (aOfficerLevel !== bOfficerLevel) {
      if (aOfficerLevel === '?') {
        return 1;
      } else if (bOfficerLevel === '?') {
        return -1;
      } else {
        return aOfficerLevel - bOfficerLevel;
      }
    }
    return a.displayName.localeCompare(b.displayName);
  };

  const classes = useStyles();

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth='sm'>
      <div className={classes.inputContainer}>
        <TextField
          value={search.query}
          onChange={queryDatabase}
          autoFocus
          fullWidth
          placeholder='Search users'
          type='search'
          variant='outlined'
        />
        <Typography className={classes.filterLabel} variant='subtitle2' color='textSecondary'>Search filter</Typography>
        <FormControl component='fieldset'>
          <FormGroup row>
            <FormControlLabel
              control={
                <Checkbox
                  checked={filters.levelOne}
                  onChange={() => setFilters(prevState => ({ ...prevState, levelOne: !prevState.levelOne }))}
                />
              }
              label='Level 1'
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={filters.levelTwo}
                  onChange={event => setFilters(prevState => ({ ...prevState, levelTwo: !prevState.levelTwo }))}
                />
              }
              label='Level 2'
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={filters.levelThree}
                  onChange={event => setFilters(prevState => ({ ...prevState, levelThree: !prevState.levelThree }))}
                />
              }
              label='Level 3'
            />
          </FormGroup>
        </FormControl>
      </div>
      <DialogContent className={classes.results}>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell align='right'>Officer level</TableCell>
              {
                showHours &&
                <TableCell align='right'>{`Hours (${week})`}</TableCell>
              }
            </TableRow>
          </TableHead>
          <TableBody>
            {search.query.length > 0
              ? search.results
                .sort(sortUsers)
                .map(user => (
                  <TableRow
                    className={user.shiftApplicant ? classes.shiftApplicant : undefined}
                    key={user.userId}
                    onClick={() => onUserClick(user.userId)}
                    hover
                  >
                    <TableCell>{`${user.displayName}${user.shiftApplicant ? ' (requested to work this shift)' : ''}`}</TableCell>
                    <TableCell align='right'>{getUserLevelAvatar(user.officerLevel)}</TableCell>
                    {showHours && (
                      <TableCell align='right'>{user.scheduledHours ? user.scheduledHours.toFixed(2) : 0}</TableCell>
                    )}
                  </TableRow>
                ))
              : users
                .filter(user => passesFilter(user))
                .sort(sortUsers)
                .map(user => (
                  <TableRow
                    className={user.shiftApplicant ? classes.shiftApplicant : undefined}
                    key={user.userId}
                    onClick={() => onUserClick(user.userId)}
                    hover
                  >
                    <TableCell>{`${user.displayName}${user.shiftApplicant ? ' (shift applicant)' : ''}`}</TableCell>
                    <TableCell align='right'>{getUserLevelAvatar(user.officerLevel)}</TableCell>
                    {
                      showHours && 
                      <TableCell align='right'>{user.scheduledHours ? user.scheduledHours.toFixed(2) : 0}</TableCell>
                    }
                  </TableRow>
                ))
            }
          </TableBody>
        </Table>
      </DialogContent>
      <DialogActions>
        <Button color='primary' onClick={onClose}>Cancel</Button>
      </DialogActions>
    </Dialog>
  );
};

const useStyles = makeStyles(theme => ({
  inputContainer: {
    margin: theme.spacing(2)
  },
  filterLabel: {
    marginTop: 12
  },
  results: {
    padding: 0
  },
  shiftApplicant: {
    backgroundColor: colors.KONTRA_BLUE_LIGHT
  }
}));

export default UsersSearchDialog;